import React from "react"
import {
  Box,
  Typography,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DownIcon,
  useTheme
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"

import { Reimbursement } from "#/types/reimbursement"

interface DirectPayAmountBreakdownProps {
  amount: number
  eligibleAmount: number
  amountAppliedToDeductible?: number | null
  amountAppliedToCostShare?: number | null
  formatCurrency: (value: number) => string
}

// Renders "Carrot pays / You pay / (deductible, cost share, amount-over) bullets".
// Shared by the live amounts block and the Original-amounts accordion.
const DirectPayAmountBreakdown = ({
  amount,
  eligibleAmount,
  amountAppliedToDeductible,
  amountAppliedToCostShare,
  formatCurrency
}: DirectPayAmountBreakdownProps) => {
  const intl = useIntl()
  const theme = useTheme()

  const amountAppliedToDeductibleString = amountAppliedToDeductible ? formatCurrency(amountAppliedToDeductible) : null
  const amountAppliedToCostShareString = amountAppliedToCostShare ? formatCurrency(amountAppliedToCostShare) : null
  const amountOverAvailableFunds =
    eligibleAmount - amount - (amountAppliedToDeductible ?? 0) - (amountAppliedToCostShare ?? 0)
  const amountOverAvailableFundsString = amountOverAvailableFunds ? formatCurrency(amountOverAvailableFunds) : null

  return (
    <>
      <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
        <FormattedMessage defaultMessage="Carrot pays: {amount}" values={{ amount: formatCurrency(amount) }} />
      </Typography>
      <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
        <FormattedMessage
          defaultMessage="You pay: {memberAmount}"
          values={{ memberAmount: formatCurrency(eligibleAmount - amount) }}
        />
      </Typography>
      <Box paddingLeft={theme.tokens.spacing.sm}>
        <UnorderedList disablePadding={true}>
          {amountAppliedToDeductibleString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Applied to deductible: {amountAppliedToDeductibleString}" },
                  { amountAppliedToDeductibleString }
                )}
              />
            </UnorderedListItem>
          )}
          {amountAppliedToCostShareString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Applied to cost share: {amountAppliedToCostShareString}" },
                  { amountAppliedToCostShareString }
                )}
              />
            </UnorderedListItem>
          )}
          {amountOverAvailableFundsString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Amount over available funds: {amountOverAvailableFundsString}" },
                  { amountOverAvailableFundsString }
                )}
              />
            </UnorderedListItem>
          )}
        </UnorderedList>
      </Box>
    </>
  )
}

interface DirectPayAmountsProps {
  transaction: Reimbursement
  formatCurrency: (value: number) => string
}

export const DirectPayAmounts = ({ transaction, formatCurrency }: DirectPayAmountsProps) => {
  const theme = useTheme()

  // DirectPayAmounts only renders for DirectPay expenses (shouldShowFullContent === isDirectPayExpense), so the parent's isDirectPayExpense guard is implicit here.
  const hasAdjustment =
    !!transaction.adjustmentDatetime && transaction.originalAmount != null && transaction.originalEligibleAmount != null

  // Refund vs owe-more
  const memberNewPays = hasAdjustment ? transaction.eligibleAmount - transaction.amount : 0
  const memberOriginalPays = hasAdjustment ? transaction.originalEligibleAmount - transaction.originalAmount : 0
  const adjustmentDifference = memberNewPays - memberOriginalPays
  const memberGetsRefund = adjustmentDifference < 0
  const memberOwesMore = adjustmentDifference > 0

  return (
    <>
      <Box>
        {hasAdjustment ? (
          <>
            <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xxs}>
              <FormattedMessage defaultMessage="Updated amounts" />
            </Typography>
            <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
              <FormattedMessage
                defaultMessage="Total: {eligibleAmount}"
                values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
              />
            </Typography>
          </>
        ) : (
          <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xxs}>
            <FormattedMessage
              defaultMessage="Amount: {eligibleAmount}"
              values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
            />
          </Typography>
        )}
        <DirectPayAmountBreakdown
          amount={transaction.amount}
          eligibleAmount={transaction.eligibleAmount}
          amountAppliedToDeductible={transaction.amountAppliedToDeductible}
          amountAppliedToCostShare={transaction.amountAppliedToCostShare}
          formatCurrency={formatCurrency}
        />

        {/* Adjustment explanation text */}
        {hasAdjustment && memberGetsRefund && (
          <Typography variant="body1" fontStyle="italic" marginTop={theme.tokens.spacing.sm}>
            <FormattedMessage
              defaultMessage="You can expect a refund of {refundAmount} from your provider. Please check your email for more information."
              values={{ refundAmount: formatCurrency(Math.abs(adjustmentDifference)) }}
            />
          </Typography>
        )}
        {hasAdjustment && memberOwesMore && (
          <Typography variant="body1" fontStyle="italic" marginTop={theme.tokens.spacing.sm}>
            <FormattedMessage
              defaultMessage="Your provider will collect an additional payment of {additionalAmount} from you. Please check your email for more information."
              values={{ additionalAmount: formatCurrency(adjustmentDifference) }}
            />
          </Typography>
        )}
      </Box>

      {/* Original amounts (shown only for adjusted transactions) */}
      {hasAdjustment && (
        <Accordion defaultExpanded sx={{ "&::before": { display: "none" } }}>
          <AccordionSummary
            expandIcon={<DownIcon />}
            aria-controls="original-amounts-content"
            id="original-amounts-header"
          >
            <Typography variant="body1" fontWeight="bold" color="secondary">
              <FormattedMessage defaultMessage="Original amounts" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="Total: {eligibleAmount}"
                  values={{ eligibleAmount: formatCurrency(transaction.originalEligibleAmount) }}
                />
              </Typography>
              <DirectPayAmountBreakdown
                amount={transaction.originalAmount}
                eligibleAmount={transaction.originalEligibleAmount}
                amountAppliedToDeductible={transaction.originalAmountAppliedToDeductible}
                amountAppliedToCostShare={transaction.originalAmountAppliedToCostShare}
                formatCurrency={formatCurrency}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}
